import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from 'api';
import AuthContext from 'context/auth-context';

const getTags = async (token) => {
  const url = 'admin/tags';
  const data = await apiGet({ url, token });
  return data;
};

const getTag = async (token, id) => {
  const url = `admin/tags/${id}`;
  const data = await apiGet({ url, token });
  return data;
};

const createTag = async (token, params) => {
  const url = 'admin/tags';
  const data = await apiPost({ url, token, objectName: 'tag', params });
  return data;
};

const updateTag = async (token, id, params) => {
  const url = `admin/tags/${id}`;
  const data = await apiPatch({ url, token, objectName: 'tag', params });
  return data;
};

const deleteTag = async (token, id) => {
  const url = `admin/tags/${id}`;
  const data = await apiDelete({ url, token });
  return data;
};

export function useAdminTags({ options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getTags(token);
  const queryKey = 'admin-tags';
  return useQuery(queryKey, queryFn, { ...options });
}

export function useAdminTag({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getTag(token, id);
  const queryKey = ['admin-tag', id];
  return useQuery(queryKey, queryFn, { enabled: !!id, ...options });
}

export function useCreateAdminTag({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => createTag(token, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-tags');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useUpdateAdminTag({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => updateTag(token, id, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-tags');
        queryClient.invalidateQueries(['admin-tag', id]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useDeleteAdminTag({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (id) => deleteTag(token, id),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-tags');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}
